/**
 * Which routes wear the alerts chrome, and what the portal's tab bar holds.
 *
 * ONE LIST, TWO READERS. `SiteChromeSlot` (root layout) hides the editorial
 * site header/footer on these paths; `PortalNav` marks the active tab from
 * the same data. When they were separate lists the first new portal route
 * would have shipped with both chromes stacked, which is exactly the kind of
 * defect that has no build error and no obvious symptom until someone looks
 * at the page.
 *
 * These live in `lib/` rather than beside the components because a route
 * group's own layout cannot see the root layout's, and both need this.
 */

/**
 * The authenticated portal's top-level URLs.
 *
 * `(portal)` is a route group, so it contributes nothing to the URL — every
 * page inside it serves a TOP-LEVEL path. That is why this list reads
 * `/history` and not `/portal/history`, and why it has to be written down at
 * all: nothing about the file path makes these recognisable as portal routes
 * from the root layout.
 */
export const PORTAL_PATHS = [
  "/dashboard",
  "/history",
  "/subscriptions",
  "/settings",
] as const;

/**
 * The portal tab bar (V03 Figma, `AlertSettings` 156:418 and siblings).
 *
 * THREE TABS, AND `/dashboard` IS DELIBERATELY NOT ONE OF THEM. The V03
 * settings page absorbs the read-only summary card the dashboard existed to
 * show (email / watching / radius / tier / status), so a "Dashboard" tab
 * would send a reader to a second copy of what they are already looking at.
 * The route itself stays — it is reachable, wears the same chrome, and any
 * email that already points at it still lands somewhere sensible — it simply
 * is not advertised in the nav. Removing it is a separate call about a URL
 * that has already been sent to people.
 */
export const PORTAL_TABS = [
  { href: "/history", label: "Alert history" },
  { href: "/subscriptions", label: "Subscriptions" },
  { href: "/settings", label: "Settings" },
] as const;

/**
 * Does this path wear the alerts chrome (bare wordmark + teal footer) rather
 * than the editorial site chrome?
 *
 * Prefix-matched, not equality-matched: `/alerts/subscribe` and a future
 * `/settings/billing` must both answer yes, and answering no would stack the
 * two chromes on top of each other rather than fail visibly.
 */
export function usesAlertsChrome(pathname: string | null): boolean {
  if (!pathname) return false;
  if (pathname === "/alerts" || pathname.startsWith("/alerts/")) return true;
  return PORTAL_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}
